import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * Timeline — vertical on mobile, horizontal stepped on desktop.
 * Emerald connector line draws in on scroll.
 *
 * Props:
 *   steps: [{ label, description, icon?: ReactNode }]
 */
export const Timeline = ({ steps = [], className }) => {
  return (
    <div className={cn("relative w-full", className)} data-testid="timeline">
      {/* DESKTOP: horizontal */}
      <div className="relative hidden lg:block">
        {/* Connector line (drawn) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-[6%] right-[6%] top-[42px] h-px overflow-hidden"
        >
          <motion.div
            className="h-full origin-left"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(0,230,118,0.5) 20%, rgba(0,230,118,0.6) 80%, transparent 100%)",
            }}
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>

        <ol className="grid grid-cols-6 gap-4">
          {steps.map((s, i) => (
            <motion.li
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.5,
                delay: 0.08 * i,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="relative flex flex-col items-center text-center"
              data-testid={`timeline-step-${i + 1}`}
            >
              {/* Number chip */}
              <div className="relative z-10 flex h-[84px] w-[84px] items-center justify-center rounded-2xl border border-white/[0.06] bg-bg-raised">
                <span className="font-mono text-xl font-semibold text-brand-emerald">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h4 className="mt-4 font-display text-base font-semibold text-white">
                {s.label}
              </h4>
              <p className="mt-2 max-w-[180px] text-xs leading-relaxed text-text-secondary">
                {s.description}
              </p>
            </motion.li>
          ))}
        </ol>
      </div>

      {/* MOBILE / TABLET: vertical */}
      <ol className="relative lg:hidden">
        {/* Vertical connector */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-[42px] top-4 bottom-4 w-px overflow-hidden"
        >
          <motion.div
            className="h-full origin-top"
            style={{
              background:
                "linear-gradient(180deg, transparent 0%, rgba(0,230,118,0.5) 10%, rgba(0,230,118,0.6) 90%, transparent 100%)",
            }}
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 1.4 }}
          />
        </div>

        {steps.map((s, i) => (
          <motion.li
            key={s.label}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, delay: 0.06 * i }}
            className="relative flex items-start gap-5 pb-8 last:pb-0"
          >
            <div className="relative z-10 flex h-[84px] w-[84px] flex-shrink-0 items-center justify-center rounded-2xl border border-white/[0.06] bg-bg-raised">
              <span className="font-mono text-lg font-semibold text-brand-emerald">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="flex flex-col pt-2">
              <h4 className="font-display text-lg font-semibold text-white">
                {s.label}
              </h4>
              <p className="mt-1 max-w-md text-sm leading-relaxed text-text-secondary">
                {s.description}
              </p>
            </div>
          </motion.li>
        ))}
      </ol>
    </div>
  );
};

export default Timeline;
